import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ApiError, errorMessage} from './api';
import {useAuth} from './auth';
import {
  AccessSnapshot,
  AdminNotice,
  AssignmentRow,
  DelegationRow,
  Empty,
  Notice,
  Spinner,
  StatusChip,
} from './components';
import {AuthorBoundary} from './components/AuthorBoundary';
import {readOnlyReason, useCapacity} from './components/Capacity';
import {CopyCode} from './components/CopyCode';
import {Icon} from './components/Icon';
import {IdentityCell} from './components/IdentityCell';
import {PermissionTable} from './components/PermissionTable';
import type {Catalogue, ProfileHead, StaffAccess, StaffPreview, StaffReceipt, StaffProposal, UserDetail} from './types';
import type {Identity} from './utils';
import {
  cachedIdentities,
  cachedIdentity,
  clearIdentityCache,
  cloneStaff,
  effectiveRows,
  formatDate,
  isAdmin,
  mapWithLimit,
  moduleLabels,
  nextAssignmentId,
  rememberIdentity,
  scopeLabel,
  toolsFor,
  unresolvedIdentity,
} from './utils';

type EditorData = StaffProposal;

/** Au plus six lectures de détail en vol : la liste ne renvoie pas l'e-mail, la page ne bloque pas. */
const IDENTITY_CONCURRENCY = 6;

export function UsersPage() {
  const {api, gate, dataEpoch, refreshAccess} = useAuth();
  const [users, setUsers] = useState<Array<StaffAccess & {id: string}>>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [missing, setMissing] = useState('');
  const [query, setQuery] = useState('');
  const [profiles, setProfiles] = useState<ProfileHead[]>([]);
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [identities, setIdentities] = useState<Map<string, Identity>>(() => cachedIdentities());
  const attempted = useRef(new Set<string>());

  useEffect(() => {
    clearIdentityCache();
    attempted.current = new Set();
    setIdentities(new Map());
  }, [dataEpoch]);

  const resolveIdentities = useCallback(async (uids: string[]) => {
    if (!api) return;
    const todo = uids.filter(uid => !cachedIdentity(uid) && !attempted.current.has(uid));
    if (!todo.length) return;
    for (const uid of todo) attempted.current.add(uid);
    const snap = gate.snapshot();
    await mapWithLimit(todo, IDENTITY_CONCURRENCY, async uid => {
      const value = await api.user(uid, snap.signal);
      rememberIdentity({uid, email: value.email, displayName: value.displayName, resolved: true});
      if (gate.accepts(snap.generation)) setIdentities(cachedIdentities());
    });
    if (gate.accepts(snap.generation)) setIdentities(cachedIdentities());
  }, [api, gate]);

  const loadUsers = useCallback(async (cursor?: string, append = false) => {
    if (!api) return;
    const snap = gate.snapshot();
    setListLoading(true); setListError('');
    try {
      const [page, profilePage, catalogueResult] = await Promise.all([
        api.users(cursor, snap.signal), api.profiles(undefined, snap.signal), api.catalogue(snap.signal),
      ]);
      if (!gate.accepts(snap.generation)) return;
      setUsers(previous => append ? [...previous, ...page.items] : page.items);
      setNextCursor(page.nextCursor);
      setProfiles(profilePage.items);
      setCatalogue(catalogueResult);
      void resolveIdentities(page.items.map(item => item.uid));
    } catch (caught) {
      if (!gate.accepts(snap.generation)) return;
      setListError(errorMessage(caught));
      if (caught instanceof ApiError && [401, 403].includes(caught.status)) void refreshAccess();
    } finally { if (gate.accepts(snap.generation)) setListLoading(false); }
  }, [api, gate, refreshAccess, resolveIdentities]);

  useEffect(() => { void loadUsers(); }, [dataEpoch, loadUsers]);

  /** La liste garde exactement la page rendue par le serveur : sélectionner un compte ne la remanie pas. */
  const loadDetail = useCallback(async (uid: string, refreshRow = false) => {
    if (!api) return;
    const snap = gate.snapshot(); setDetailError(''); setMissing(''); setDetailLoading(true);
    try {
      const value = await api.user(uid, snap.signal);
      if (!gate.accepts(snap.generation)) return;
      setDetail(value);
      // After an audited apply/conflict, refresh the existing row without changing
      // the page order, cursor, or adding an out-of-page lookup result.
      if (refreshRow && value.staff) setUsers(previous => previous.map(user =>
        user.uid === value.uid ? {...value.staff!, id: user.id} : user));
      rememberIdentity({uid: value.uid, email: value.email, displayName: value.displayName, resolved: true});
      attempted.current.add(value.uid);
      setIdentities(cachedIdentities());
    } catch (caught) { if (gate.accepts(snap.generation)) setDetailError(errorMessage(caught)); }
    finally { if (gate.accepts(snap.generation)) setDetailLoading(false); }
  }, [api, gate]);

  const search = async () => {
    if (!api || !query.trim()) return;
    const term = query.trim();
    const snap = gate.snapshot();
    setDetailLoading(true); setDetailError(''); setMissing('');
    try {
      const value = await api.lookup(term.includes('@') ? {email: term} : {uid: term}, snap.signal);
      if (!gate.accepts(snap.generation)) return;
      setDetail(value);
      rememberIdentity({uid: value.uid, email: value.email, displayName: value.displayName, resolved: true});
      attempted.current.add(value.uid);
      setIdentities(cachedIdentities());
    } catch (caught) {
      if (!gate.accepts(snap.generation)) return;
      // « Aucun compte exact » appartient à la fiche, pas à la liste : la page chargée reste valide.
      if (caught instanceof ApiError && (caught.code === 'account_not_found' || caught.status === 404)) { setDetail(null); setMissing(term); }
      else setDetailError(errorMessage(caught));
    } finally { if (gate.accepts(snap.generation)) setDetailLoading(false); }
  };

  const identityFor = (uid: string) => identities.get(uid) || unresolvedIdentity(uid);
  const outOfPage = !!detail && !users.some(user => user.uid === detail.uid);

  return <div className="two">
    <section className={listLoading && !users.length ? 'panel is-loading' : 'panel'}>
      <div className="panel-head"><h2>Comptes staff</h2><span className="count">{users.length} sur cette page</span></div>
      <form className="search" role="search" onSubmit={event => {event.preventDefault(); void search();}}>
        <label className="field"><Icon name="search" size={14} />
          <input aria-label="Recherche exacte par e-mail ou UID" placeholder="E-mail exact ou UID" value={query} onChange={event => setQuery(event.target.value)} />
        </label>
        <button className="btn">Rechercher</button>
      </form>
      <p className="help">Recherche exacte : l’adresse complète ou l’UID. Un compte Firebase sans accès staff s’ouvre aussi, pour lui en donner.</p>
      {listError && <Notice tone="danger">{listError}</Notice>}
      {listLoading && !users.length ? <Spinner /> : users.length ? <ul className="list" role="listbox" aria-label="Comptes staff">
        {users.map(user => <li key={user.id} role="presentation">
          <button type="button" role="option" aria-selected={detail?.uid === user.uid} className={detail?.uid === user.uid ? 'row sel' : 'row'} onClick={() => void loadDetail(user.uid)}>
            <IdentityCell identity={identityFor(user.uid)} pending={!attempted.current.has(user.uid)} copy={false} />
            <span className="cell prof">{isAdmin(user) ? 'Admin — tous les droits actuels et futurs' : user.assignments.length ? user.assignments.map(item => `${item.profileId} v${item.profileVersion}`).join(', ') : 'Aucun profil'}</span>
            <StatusChip status={user.status} />
          </button>
        </li>)}
      </ul> : <Empty>Aucun compte staff dans cette page.</Empty>}
      <div className="list-foot">
        {nextCursor && <button type="button" className="btn" disabled={listLoading} onClick={() => void loadUsers(nextCursor, true)}>Charger la page suivante</button>}
        <p className="order-note">Ordre par identifiant, pas par date</p>
      </div>
    </section>
    <section className={detailLoading ? 'panel detail is-loading' : 'panel detail'}>
      {detailError && <Notice tone="danger">{detailError}</Notice>}
      {missing && <Notice tone="warning"><strong>Aucun compte exact.</strong> Aucun compte Firebase ne correspond à « {missing} ». La recherche exige l’adresse complète ou l’UID.</Notice>}
      {detailLoading && !detail ? <Spinner label="Ouverture de la fiche…" />
        : detail ? <UserDetailPanel
            detail={detail}
            identity={identityFor(detail.uid)}
            outOfPage={outOfPage}
            profiles={profiles}
            catalogue={catalogue}
            onReload={() => loadDetail(detail.uid, true)}
          />
        : !missing ? <Empty>Sélectionnez un compte staff, ou recherchez un compte Firebase existant.</Empty> : null}
    </section>
  </div>;
}

function UserDetailPanel({detail, identity, outOfPage, profiles, catalogue, onReload}: {
  detail: UserDetail; identity: Identity; outOfPage: boolean; profiles: ProfileHead[]; catalogue: Catalogue | null; onReload(): Promise<void>;
}) {
  const {api, gate, me, refreshAccess} = useAuth();
  const capacity = useCapacity();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditorData>(() => cloneStaff(detail.staff, detail.uid));
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<StaffPreview | null>(null);
  const [applyRequestId, setApplyRequestId] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<StaffReceipt | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const definitions = catalogue?.permissions || [];
  const canManage = !!me?.effectivePermissions.some(grant => grant.permission === 'iam.users.manage')
    && (!isAdmin(detail.staff) || me.admin === true);
  const canSetAdmin = me?.admin === true && me.status === 'active' && detail.uid !== me.uid;
  const mutationReady = !!capacity?.ready;
  const changed = JSON.stringify(draft) !== JSON.stringify(cloneStaff(detail.staff, detail.uid));

  useEffect(() => { setReceipt(null); setReason(''); setError(''); }, [detail.uid]);
  useEffect(() => { setDraft(cloneStaff(detail.staff, detail.uid)); setEditing(false); setPreview(null); setApplyRequestId(null); }, [detail]);

  const tools = detail.disabled ? [] : toolsFor(detail.staff, detail.effectivePermissions);
  const profileNames = useMemo(() => new Map(detail.profiles.map(profile => [`${profile.profileId}/${profile.version}`, profile.name])), [detail.profiles]);
  const rows = useMemo(() => effectiveRows(detail.effectivePermissions, definitions), [detail.effectivePermissions, definitions]);

  const buildPreview = async () => {
    if (!api) return;
    setBusy(true); setError(''); setReceipt(null);
    const snap = gate.snapshot();
    try {
      const result = await api.preview(detail.uid, detail.staff?.revision || 0, draft, reason, snap.signal);
      if (gate.accepts(snap.generation)) { setPreview(result); setApplyRequestId(crypto.randomUUID()); }
    } catch (caught) {
      if (!gate.accepts(snap.generation)) return;
      setPreview(null); setError(errorMessage(caught));
      if (caught instanceof ApiError && caught.status === 409) await onReload();
      if (caught instanceof ApiError && ['reauth_required', 'mfa_required', 'verified_email_required'].includes(caught.code)) await refreshAccess();
    } finally { if (gate.accepts(snap.generation)) setBusy(false); }
  };

  const applyPreview = async () => {
    if (!api || !preview || !applyRequestId) return;
    setBusy(true); setError('');
    const snap = gate.snapshot();
    try {
      const result = await api.apply(preview.previewId, applyRequestId, snap.signal);
      if (!gate.accepts(snap.generation)) return;
      setReceipt(result); setPreview(null); setApplyRequestId(null); setEditing(false);
      await onReload();
    } catch (caught) {
      if (!gate.accepts(snap.generation)) return;
      setError(errorMessage(caught));
      if (caught instanceof ApiError && caught.status === 409) { setPreview(null); setApplyRequestId(null); await onReload(); }
      if (caught instanceof ApiError && ['reauth_required', 'mfa_required', 'verified_email_required'].includes(caught.code)) { setPreview(null); setApplyRequestId(null); await refreshAccess(); }
    } finally { if (gate.accepts(snap.generation)) setBusy(false); }
  };

  const status = detail.staff?.status || 'none';
  const mfaChip = detail.disabled ? <span className="chip warn">Compte Firebase désactivé</span> : null;

  return <>
    {outOfPage && <Notice tone="info"><strong>Compte hors de la page courante.</strong> Ce compte a été trouvé par recherche exacte : il n’apparaît pas dans la page de liste chargée.</Notice>}
    <header className="detail-head">
      <IdentityCell identity={identity} big />
      <div className="head-right"><StatusChip status={status} />{mfaChip}</div>
    </header>
    {isAdmin(detail.staff) && <AdminNotice active={detail.staff?.status === 'active' && !detail.disabled} />}
    <dl className="facts">
      <div><dt>Compte Firebase</dt><dd>{detail.disabled ? 'Désactivé' : 'Actif'}</dd></div>
      <div><dt>Outils ouverts</dt><dd>{tools.map(tool => moduleLabels[tool] || tool).join(', ') || 'Aucun'}</dd></div>
      <div><dt>Profils</dt><dd>{detail.staff?.assignments.length ? detail.staff.assignments.map(item => `${profileNames.get(`${item.profileId}/${item.profileVersion}`) || item.profileId} v${item.profileVersion}`).join(', ') : 'Aucun'}</dd></div>
      <div><dt>Révision</dt><dd>{detail.staff?.revision || 0}</dd></div>
    </dl>
    {canManage && !editing && !preview && <div className="actions">
      <button type="button" className="btn primary" disabled={!mutationReady} title={mutationReady ? undefined : readOnlyReason} onClick={() => { setDraft(cloneStaff(detail.staff, detail.uid)); setError(''); setEditing(true); }}>Modifier les accès</button>
      <span className="muted small">Toute modification passe par un aperçu serveur puis une confirmation. Motif obligatoire.</span>
    </div>}
    {detail.disabled && <Notice tone="danger"><strong>Compte Firebase désactivé.</strong> Ses droits configurés restent visibles pour permettre leur retrait. Le serveur accepte seulement une suspension ou une réduction des affectations et du plafond ; toute concession ou réactivation est refusée.</Notice>}
    {receipt && <Notice tone="success"><strong>Changement confirmé par le serveur.</strong> Révision {receipt.revision}, {formatDate(receipt.appliedAt)}. Reçu <code>{receipt.auditId}</code><CopyCode value={receipt.auditId} label="le reçu" /></Notice>}
    {error && <Notice tone="danger">{error}</Notice>}

    <section className="block">
      <h3>Affectations <span className="muted">{detail.staff?.assignments.length || 0}</span></h3>
      {detail.staff?.assignments.length ? <ul className="cards">{detail.staff.assignments.map(item => <li key={item.assignmentId}>
        <strong>{profileNames.get(`${item.profileId}/${item.profileVersion}`) || item.profileId}</strong>
        <span className="sub">{item.profileId} · utilise la version v{item.profileVersion}</span>
        <span className="sub">Périmètre : {scopeLabel(item.scope)} · id {item.assignmentId}</span>
      </li>)}</ul> : <p className="muted small">Aucune affectation.</p>}
    </section>

    <section className="block">
      <h3>Plafond de délégation <span className="muted">{detail.staff?.delegation.length || 0} permissions</span></h3>
      <p className="muted small">{isAdmin(detail.staff) ? 'Le statut admin donne un plafond implicite complet, y compris les permissions futures. Le plafond explicite ci-dessous reste conservé en cas de retrait du statut.' : 'Ce compte peut accorder à d’autres au plus ces permissions, sur ces périmètres.'} Il ne peut pas modifier ses propres accès.</p>
    </section>

    <section className="block">
      <h3>Droits effectifs et origine <span className="muted">{rows.length}</span></h3>
      <p className="note-inline"><Icon name="alert" size={13} /> Droits natifs des outils non migrés : ni lus ni modifiés ici. Une suspension bloque seulement les outils déjà rattachés à ce portail.</p>
      {rows.length ? <PermissionTable rows={rows} showOrigin /> : <Empty>Aucun droit effectif.</Empty>}
    </section>

    {editing && !preview && <form className="access-editor" onSubmit={event => {event.preventDefault(); void buildPreview();}}>
      <div className="section-title"><h3>Proposition</h3><button type="button" className="text-button" onClick={() => setEditing(false)}>Annuler</button></div>
      <label>Statut<select value={draft.status} onChange={event => setDraft({...draft, status: event.target.value as EditorData['status']})}><option value="active">Actif</option><option value="suspended">Suspendu</option></select></label>
      {canSetAdmin && <label><input type="checkbox" checked={draft.admin === true} disabled={detail.disabled && !isAdmin(detail.staff)} onChange={event => setDraft({...draft, admin: event.target.checked})} /> Admin — tous les droits actuels et futurs</label>}
      {canSetAdmin && <p className="field-help">Ce statut est global et inclut la délégation complète. Les profils et le plafond explicites sont conservés.</p>}
      <div className="section-title"><h4>Affectations</h4><button type="button" className="btn sm" disabled={!profiles.length} onClick={() => setDraft({...draft, assignments: [...draft.assignments, {assignmentId: nextAssignmentId(draft.assignments), profileId: profiles[0]?.profileId || '', profileVersion: profiles[0]?.latestVersion || 1, scope: {kind: 'global'}}]})}><Icon name="plus" size={13} /> Ajouter</button></div>
      {draft.assignments.map((assignment, index) => <AssignmentRow key={`${assignment.assignmentId}-${index}`} value={assignment} profiles={profiles} onChange={value => setDraft({...draft, assignments: draft.assignments.map((item, itemIndex) => itemIndex === index ? value : item)})} onRemove={() => setDraft({...draft, assignments: draft.assignments.filter((_, itemIndex) => itemIndex !== index)})} />)}
      <div className="section-title"><h4>Plafond de délégation</h4><button type="button" className="btn sm" disabled={!definitions.length} onClick={() => setDraft({...draft, delegation: [...draft.delegation, {permission: definitions[0].permission, scope: {kind: 'global'}}]})}><Icon name="plus" size={13} /> Ajouter</button></div>
      {draft.delegation.map((grant, index) => <DelegationRow key={`${grant.permission}-${index}`} value={grant} definitions={definitions} onChange={value => setDraft({...draft, delegation: draft.delegation.map((item, itemIndex) => itemIndex === index ? value : item)})} onRemove={() => setDraft({...draft, delegation: draft.delegation.filter((_, itemIndex) => itemIndex !== index)})} />)}
      <label>Motif du changement<textarea minLength={3} maxLength={1000} value={reason} onChange={event => setReason(event.target.value)} placeholder="Pourquoi ces droits changent-ils ?" required /></label>
      <button className="btn primary" disabled={busy || !mutationReady || !changed}>{busy ? 'Préparation…' : 'Générer l’aperçu serveur'}</button>
      {!changed && <p className="field-help">Modifiez le statut, une affectation ou le plafond avant de demander un aperçu.</p>}
      {!mutationReady && <p className="field-help">{readOnlyReason}</p>}
    </form>}

    {preview && <div className="preview-card">
      <div className="panel-head"><h3>Confirmer ce changement</h3><span className="count">expire à {new Date(preview.expiresAt).toLocaleTimeString('fr-FR')}</span></div>
      <p><strong>Motif :</strong> {preview.reason}</p>
      <div className="snapshot-grid"><AccessSnapshot title="Avant" value={preview.before} /><AccessSnapshot title="Après" value={preview.proposed} /></div>
      <details><summary>Empreinte de l’aperçu</summary><code className="hash">{preview.hash}</code></details>
      <div className="actions">
        <button type="button" className="btn" onClick={() => { setPreview(null); setApplyRequestId(null); }}>Revenir à l’édition</button>
        <button type="button" className="btn primary" disabled={busy || !mutationReady || !applyRequestId} onClick={() => void applyPreview()}>{busy ? 'Application…' : 'Confirmer et appliquer'}</button>
      </div>
    </div>}

    <AuthorBoundary />
  </>;
}
